import { cn } from "@/lib/utils"

interface Props {
  /**
   * Uppercase label in the top-left corner, e.g. "Onboarding". It sits left
   * because the opposite corner belongs to the wizard's close button.
   */
  badge: string
  /**
   * How much vertical room the gradient gets. The reveal is tallest, since the
   * match number sits inside it.
   */
  size?: "welcome" | "question" | "reveal"
  children?: React.ReactNode
}

const HEIGHTS = {
  welcome: "min-h-[110px] items-end",
  question: "min-h-[88px] items-center",
  reveal: "min-h-[206px] flex-col justify-center pt-[34px] pb-[30px]",
} as const

/** The gradient band across the top of the wizard. */
export function OnboardingHero({ badge, size = "question", children }: Props) {
  return (
    <div
      className={cn(
        "bg-onboarding-hero relative flex overflow-hidden px-9 pt-6 pb-5",
        HEIGHTS[size],
      )}
    >
      <span className="absolute top-5 left-9 z-20 text-[11px] font-semibold tracking-[0.08em] text-white/85 uppercase">
        {badge}
      </span>

      {/* Soft highlights, matching the mockup's two radial glows. */}
      <span
        aria-hidden
        className="bg-onboarding-glow-light pointer-events-none absolute -top-[90px] -right-[50px] size-[220px]"
      />
      <span
        aria-hidden
        className="bg-onboarding-glow-primary pointer-events-none absolute -bottom-[110px] -left-10 size-[200px]"
      />

      {children}

      {/*
       * Melts the gradient into the white body below it. Fades from white at
       * zero alpha, not `transparent`: Tailwind v4 interpolates gradients in
       * oklab, where `transparent` is black at zero alpha, so fading from it
       * greys the middle of the ramp and leaves a visible band.
       */}
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute right-0 bottom-0 left-0 bg-gradient-to-b from-white/0 to-white",
          size === "reveal" ? "h-[26px]" : "h-16",
        )}
      />
    </div>
  )
}
